import {
  MAX_RECENT_TRADES_LENGTH,
  Trade,
  tradeMatchesFilters,
} from 'hooks/useRecentTrades'
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react'
import store from 'stores/store'
import { debounce } from 'lodash'
import { produce } from 'immer'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { SIDE_FILTER_KEY } from '@components/modals/SideFilterFormModal'
import {
  TRADE_VALUE_FILTER_KEY,
  TRADER_ADDRESS_FILTER_KEY,
} from 'utils/constants'
import { useVenueFilter } from './useSettings'
import { subscribeToTrades } from 'apis/birdeye/streaming'

const set = store.getState().set

const TradesFeedSocketProvider = ({ children }: { children: ReactNode }) => {
  const baseMint = store((s) => s.market.baseToken.mint)
  const quoteMint = store((s) => s.market.quoteToken.mint)
  const updateQueue = useRef<Trade[]>([])
  const [addressToFilter] = useLocalStorage(TRADER_ADDRESS_FILTER_KEY)
  const [valueFilter] = useLocalStorage(TRADE_VALUE_FILTER_KEY)
  const [venueFilter] = useVenueFilter()
  const [sideFilter] = useLocalStorage(SIDE_FILTER_KEY)

  // Snapshot the latest filters in a ref so the debounced batch handler can
  // read them without re-subscribing the socket on each filter change.
  const filtersRef = useRef({
    sideFilter,
    venueFilter,
    valueFilter,
    addressToFilter,
  })
  useEffect(() => {
    filtersRef.current = {
      sideFilter,
      venueFilter,
      valueFilter,
      addressToFilter,
    }
  }, [sideFilter, venueFilter, valueFilter, addressToFilter])

  const processBatchedUpdates = useCallback(() => {
    if (updateQueue.current.length === 0) return
    const trades = updateQueue.current
    updateQueue.current = []

    set(
      produce((state) => {
        for (const trade of trades) {
          if (!tradeMatchesFilters(trade, filtersRef.current)) continue
          state.trades.realtimeTrades.unshift(trade)
          if (state.trades.realtimeTrades.length > MAX_RECENT_TRADES_LENGTH) {
            state.trades.realtimeTrades.pop()
          }
          const volume = trade.size * trade.price
          if (trade.taker_side === 'buy') {
            state.trades.buyStats.volume += volume
            state.trades.buyStats.trades += 1
          } else {
            state.trades.sellStats.volume += volume
            state.trades.sellStats.trades += 1
          }
        }
      }),
    )
  }, [])

  const debouncedProcessUpdates = useMemo(
    () => debounce(processBatchedUpdates, 500, { maxWait: 1000 }),
    [processBatchedUpdates],
  )

  useEffect(() => {
    if (!baseMint || !quoteMint) return

    set((state) => {
      state.trades = {
        realtimeTrades: [],
        buyStats: { volume: 0, trades: 0 },
        sellStats: { volume: 0, trades: 0 },
      }
    })
    updateQueue.current = []

    const unsubscribe = subscribeToTrades({
      baseMint,
      quoteMint,
      onTrade: (trade) => {
        updateQueue.current.push(trade)
        debouncedProcessUpdates()
      },
    })

    return () => {
      unsubscribe()
      debouncedProcessUpdates.cancel()
      updateQueue.current = []
    }
  }, [baseMint, quoteMint, debouncedProcessUpdates])

  return <>{children}</>
}
export default TradesFeedSocketProvider
